/**
 * ImpersonationLinkModal
 * Feature: 075-link-based-impersonation
 *
 * Shows the generated impersonation URL and PIN after admin clicks
 * "Generate Impersonation Link" on a user. Admin copies the link
 * and verbally communicates the PIN to the user.
 */

import { useState, useEffect, useRef } from 'react';
import { Modal } from '../composed/Modal/Modal';
import logger from '../../utils/logger';

interface ImpersonationLinkData {
  url: string;
  pin: string;
  expiresAt: string;
  targetUsername: string;
}

interface ImpersonationLinkModalProps {
  open: boolean;
  onClose: () => void;
  data: ImpersonationLinkData | null;
}

/**
 * Calculates time remaining until expiration
 */
function useTimeRemaining(expiresAt: string | null): string {
  const [remaining, setRemaining] = useState<string>('');

  useEffect(() => {
    if (!expiresAt) return;

    const updateRemaining = () => {
      const now = Date.now();
      const expiry = new Date(expiresAt).getTime();
      const diff = expiry - now;

      if (diff <= 0) {
        setRemaining('Expired');
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateRemaining();
    const interval = setInterval(updateRemaining, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  return remaining;
}

/**
 * ImpersonationLinkModal - Displays URL and PIN for admin impersonation
 *
 * The admin copies the URL and verbally tells the user the PIN.
 * The user opens the URL in an incognito window and enters the PIN.
 */
export function ImpersonationLinkModal({
  open,
  onClose,
  data,
}: ImpersonationLinkModalProps) {
  const [copied, setCopied] = useState(false);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const timeRemaining = useTimeRemaining(data?.expiresAt || null);

  // Reset copied state when modal opens
  useEffect(() => {
    if (open) {
      setCopied(false);
    }
  }, [open]);

  const handleCopyUrl = async () => {
    if (!data?.url) return;

    try {
      await navigator.clipboard.writeText(data.url);
      setCopied(true);
      logger.info('Admin', 'Impersonation URL copied to clipboard');

      // Reset after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.warn('Admin', 'Failed to copy to clipboard', { error: err });
      // Fallback: select the input
      urlInputRef.current?.select();
    }
  };

  if (!data) return null;

  const isExpired = timeRemaining === 'Expired';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Impersonation Link Created"
      size="lg"
    >
      <div className="space-y-6">
        {/* Instructions */}
        <div className="text-sm text-muted-foreground">
          <p>
            Share the link below with <strong>{data.targetUsername}</strong>.
            They will need to enter the PIN to complete login.
          </p>
        </div>

        {/* URL Section */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Impersonation URL
          </label>
          <div className="flex gap-2">
            <input
              ref={urlInputRef}
              type="text"
              readOnly
              value={data.url}
              className="flex-1 px-3 py-2 text-sm bg-muted border border-border rounded-md font-mono"
              onClick={(e) => e.currentTarget.select()}
            />
            <button
              onClick={handleCopyUrl}
              disabled={isExpired}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                copied
                  ? 'bg-green-600 text-white'
                  : isExpired
                    ? 'bg-muted text-muted-foreground cursor-not-allowed'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        {/* PIN Section */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            PIN (tell this verbally)
          </label>
          <div className="flex items-center gap-4">
            <div className="text-3xl font-mono font-bold tracking-widest text-foreground bg-muted px-6 py-3 rounded-lg">
              {data.pin}
            </div>
            <div className="text-sm text-muted-foreground">
              <p>Do not include this in any written message.</p>
              <p>Tell the user verbally or via secure channel.</p>
            </div>
          </div>
        </div>

        {/* Expiration */}
        <div className={`text-sm ${isExpired ? 'text-destructive' : 'text-muted-foreground'}`}>
          {isExpired ? (
            <p className="font-medium">This link has expired. Generate a new one if needed.</p>
          ) : (
            <p>
              Expires in: <span className="font-mono font-medium">{timeRemaining}</span>
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium bg-muted text-foreground rounded-md hover:bg-muted/80 transition-colors"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
